<script setup>
// §8.6 RampLegend — MANDATORY alongside RampBar (§6.3). Named label + value per
// step. This is the carrier of meaning; without it the chart is non-conformant.
// The swatch is a graphical cue only — the label and value do the work. The
// "Everything else" step expands to reveal its members on click (§6.2).
import { ref } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	steps: { type: Array, required: true },
	total: { type: Number, default: 0 },
	format: { type: Function, default: (v) => String(v) },
})
const expanded = ref(false)
const pct = (v) => (props.total ? Math.round((v / props.total) * 100) : 0)
</script>

<template>
	<ul class="legend">
		<li v-for="(s, i) in steps" :key="i" class="row">
			<div class="lead">
				<span class="swatch" :style="{ background: s.color }" aria-hidden="true" />
				<button v-if="s.rest" class="rest-toggle" type="button" :aria-expanded="expanded"
					@click="expanded = !expanded">
					<LIcon name="chevron-right" class="chev" :class="{ open: expanded }" :size="13"
						aria-hidden="true" />
					{{ s.label }}
				</button>
				<span v-else class="label">{{ s.label }}</span>
			</div>
			<div class="vals">
				<span class="value num">{{ format(s.value) }}</span>
				<span class="share num">{{ pct(s.value) }}%</span>
			</div>
		</li>
		<li v-if="expanded" class="members">
			<ul>
				<li v-for="(m, j) in steps.find((s) => s.rest)?.members || []" :key="j" class="member">
					<span class="member-label">{{ m.label }}</span>
					<span class="member-value num">{{ format(m.value) }}</span>
				</li>
			</ul>
		</li>
	</ul>
</template>

<style scoped>
.legend
{
	list-style: none;
	display: flex;
	flex-direction: column;
}

.row
{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sp-4);
	padding: 7px 0;
	border-bottom: 1px solid var(--line-soft);
}

.row:last-child
{
	border-bottom: 0;
}

.lead
{
	display: flex;
	align-items: center;
	gap: var(--sp-3);
	min-width: 0;
}

.swatch
{
	width: 12px;
	height: 12px;
	border-radius: 3px;
	flex: none;
}

.label
{
	font-size: var(--text-base);
	color: var(--text);
}

.rest-toggle
{
	display: inline-flex;
	align-items: center;
	gap: 5px;
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--interactive-text);
	font-weight: 600;
	background: none;
	border: 0;
	cursor: pointer;
	min-height: var(--target-min);
}

.chev
{
	transition: transform var(--t-fast) var(--ease);
}

.chev.open
{
	transform: rotate(90deg);
}

.vals
{
	display: flex;
	align-items: baseline;
	gap: var(--sp-4);
	flex: none;
}

.value
{
	font-size: var(--text-base);
	font-weight: 600;
	color: var(--text);
}

.share
{
	font-size: var(--text-chip);
	color: var(--text-3);
	width: 34px;
	text-align: right;
}

.members ul
{
	list-style: none;
	margin-left: 24px;
	border-left: 1px solid var(--line-soft);
	padding-left: var(--sp-3);
}

.member
{
	display: flex;
	justify-content: space-between;
	gap: var(--sp-4);
	padding: 5px 0;
	font-size: var(--text-sm);
}

.member-label
{
	color: var(--text-2);
}

.member-value
{
	color: var(--text-3);
	font-weight: 600;
}
</style>
